import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import api from '../utils/api';

const JournalArticlePage = () => {
  const { schoolSlug, classSlug, year, slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticle();
  }, [slug]);

  const loadArticle = async () => {
    try {
      const response = await api.get(`/articles/journal/article/${schoolSlug}/${classSlug}/${year}/${slug}`);
      setArticle(response.data.article);
    } catch (error) {
      console.error('Error loading article:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5dc] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#f5f5dc] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-4xl font-bold">Article non trouvé</h1>
          <Link to={`/journal/${schoolSlug}/${classSlug}/${year}`} className="mt-4 inline-block underline">
            ← Retour au journal
          </Link>
        </div>
      </div>
    );
  }

  const displayContent = article.displayContent || article.correctedContent || article.content;

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{article.headline} | The Daily Globe - {schoolSlug}</title>
        <meta name="description" content={article.lead || article.subheadline || displayContent?.substring(0, 160)} />
        <meta name="author" content={article.studentName} />
        <meta property="og:title" content={article.headline} />
        <meta property="og:description" content={article.lead || article.subheadline} />
        <meta property="og:type" content="article" />
        {article.photoUrl && <meta property="og:image" content={article.photoUrl} />}
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="min-h-screen bg-[#f5f5dc]">
        {/* Header */}
        <header className="border-b-2 border-black py-3 px-4 bg-white">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <Link to={`/journal/${schoolSlug}/${classSlug}/${year}`} className="font-serif text-2xl font-black uppercase">
              THE DAILY GLOBE
            </Link>
            <Link to={`/journal/${schoolSlug}/${classSlug}/${year}`} className="text-sm hover:underline">
              ← Retour au journal
            </Link>
          </div>
        </header>

        {/* Article */}
        <motion.article 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="max-w-4xl mx-auto px-4 py-8 bg-white my-8 border border-black"
        >
          {/* Category / Date */}
          <div className="border-b border-black pb-2 mb-4 flex justify-between items-center text-sm">
            <span className="uppercase tracking-widest font-bold">{classSlug}</span>
            <span className="italic">{formatDate(article.publishedAt)}</span>
          </div>

          {/* Headline */}
          <h1 className="font-serif text-4xl md:text-5xl font-black uppercase leading-tight mb-4"
              style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}>
            {article.headline}
          </h1>

          {/* Subheadline */}
          {article.subheadline && (
            <p className="font-serif text-xl text-gray-700 italic mb-4 border-l-4 border-black pl-4">
              {article.subheadline}
            </p>
          )}

          {/* Author */}
          <div className="flex items-center gap-4 py-4 border-t border-b border-gray-300 mb-6">
            <div className="w-12 h-12 bg-black text-white rounded-full flex items-center justify-center font-bold text-lg">
              {article.studentName?.split(' ').map(n => n[0]).join('')}
            </div>
            <div>
              <p className="font-bold uppercase tracking-wide">{article.studentName}</p>
              <p className="text-sm text-gray-600">Étudiant en journalisme</p>
            </div>
          </div>

          {/* Photo */}
          {article.photoUrl && (
            <figure className="mb-6">
              <div className="border border-black">
                <img 
                  src={article.photoUrl} 
                  alt={article.headline}
                  className="w-full grayscale"
                />
              </div>
              {article.photoCaption && (
                <figcaption className="text-sm italic text-gray-600 mt-2 border-l-2 border-gray-300 pl-3">
                  {article.photoCaption}
                </figcaption>
              )}
            </figure>
          )}

          {/* Lead / Chapô */}
          {article.lead && (
            <p className="font-serif text-xl leading-relaxed mb-6 font-bold">
              {article.lead}
            </p>
          )}

          {/* Content */}
          <div className="font-serif text-lg leading-relaxed prose prose-lg max-w-none"
               style={{ 
                 fontFamily: "'Georgia', serif",
                 columnCount: 1,
                 textAlign: 'justify'
               }}>
            {displayContent?.split('\n\n').map((paragraph, index) => (
              <p key={index} className={index === 0 ? "first-letter:text-6xl first-letter:font-bold first-letter:float-left first-letter:mr-3 first-letter:mt-1" : ""}>
                {paragraph}
              </p>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-8 pt-4 border-t-2 border-black">
            <p className="text-center text-sm text-gray-600">
              Article publié dans The Daily Globe • {schoolSlug?.replace('-', ' ')} • Promotion {year}
            </p>
          </div>
        </motion.article>

        {/* Share buttons */}
        <div className="max-w-4xl mx-auto px-4 pb-8 flex justify-center gap-4">
          <button 
            onClick={() => navigator.share?.({ title: article.headline, url: window.location.href })}
            className="px-6 py-2 border-2 border-black bg-white hover:bg-black hover:text-white transition-colors font-bold text-sm uppercase"
          >
            Partager
          </button>
          <button 
            onClick={() => window.print()}
            className="px-6 py-2 border-2 border-black bg-white hover:bg-black hover:text-white transition-colors font-bold text-sm uppercase"
          >
            Imprimer
          </button>
        </div>

        {/* Footer */}
        <footer className="border-t-2 border-black bg-white py-4">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="font-serif text-sm">
              THE DAILY GLOBE • Propulsé par DailyGlobe School
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default JournalArticlePage;
